import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetNotificationDeliveryReportQuery,
  useGetNotificationDeliverySummaryQuery,
  useUpdateNotificationDeliveryHeadDataMutation,
} from "../../../../store/queries/CMS";
import {
  updateConfig,
  resetConfig,
} from "../../../../store/slices/CMS/notificationDeliverySlice";
import { updateConfig as globalUpdateConfig } from "../../../../store/slices/Global";
import { getAdminAxiosInstance } from "../../../../api/Admin";
import { toast } from "react-toastify";

// Only the key travels to the API — DeliveryReportService owns the key → status
// mapping. Sending the statuses themselves would not survive axiosBaseQuery,
// which forwards a fixed set of GET params and would flatten an array anyway.
export const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "failures", label: "Not delivered" },
  { key: "sent", label: "Sent" },
  { key: "clicked", label: "Clicked" },
  { key: "rejected", label: "Rejected" },
  { key: "failed", label: "Failed" },
  { key: "no_player_id", label: "No device" },
];

const useNotificationDeliveryReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notificationID } = useParams();
  const deliveryState = useSelector((state) => state.notificationDelivery);
  const { showEditModal } = useSelector((state) => state.global);

  const [updateDeliveryHead] = useUpdateNotificationDeliveryHeadDataMutation();

  const paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  // Filters and paging are per-notification; carrying them across would show a
  // stale page number on a report with fewer rows.
  useEffect(() => {
    dispatch(resetConfig());
  }, [notificationID, dispatch]);

  const activeFilter =
    STATUS_FILTERS.find((f) => f.key === deliveryState?.currentFilter) ??
    STATUS_FILTERS[0];

  const { data: summaryData = {}, isLoading: isSummaryLoading } =
    useGetNotificationDeliverySummaryQuery({
      notification_id: notificationID,
    });

  const {
    data: deliveryList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetNotificationDeliveryReportQuery({
    notification_id: notificationID,
    filter: activeFilter.key,
    search: deliveryState?.search,
    page: deliveryState?.currentPage,
    // axiosBaseQuery maps page_size → per_page; passing per_page is ignored.
    page_size: deliveryState?.currentPageSize,
    sort: deliveryState?.sortBy,
    sort_order: deliveryState?.sortOrder,
  });

  const notification = summaryData?.data?.notification;
  const delivery = summaryData?.data?.delivery;

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e.target.value;
        state.currentPage = 1;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(
      updateConfig((state) => {
        state.search = "";
        state.currentPage = 1;
      })
    );
  };

  const handleFilterChange = (key) => {
    dispatch(
      updateConfig((state) => {
        state.currentFilter = key;
        state.currentPage = 1;
      })
    );
  };

  const handleSort = (label) => {
    if (deliveryState.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder = deliveryState.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const handleBack = () => navigate("/cms/custom-notifications");

  /**
   * Exports whatever the current filter shows, not the whole report — the
   * downloaded file should match what is on screen.
   */
  const handleExport = async () => {
    try {
      const api = await getAdminAxiosInstance();
      const response = await api.get(
        `/notification/delivery-export/${notificationID}`,
        {
          params: { filter: activeFilter.key },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `notification-delivery-${notificationID}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to export the delivery report");
    }
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field] ?? "—"}</p>,
      status_label: (field, data) => (
        <span className={`pro-badge badge-${statusVariant(data?.status)}-outline`}>
          {data[field]}
        </span>
      ),
    };

    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  return {
    notification,
    delivery,
    deliveryList,
    deliveryState,
    activeFilter,
    statusFilters: STATUS_FILTERS,
    isLoading,
    isFetching,
    isSummaryLoading,
    showEditModal,
    paginationOptions,
    updateDeliveryHead,
    refetch,
    getRow,
    handleSearch,
    handleClearClick,
    handleFilterChange,
    handleSort,
    handlePagination,
    handlePageSize,
    handleEditColumnsClick,
    handleExport,
    handleBack,
  };
};

const statusVariant = (status) => {
  switch (status) {
    case "sent":
      return "success";
    case "clicked":
      return "primary";
    case "rejected":
    case "failed":
      return "danger";
    case "no_player_id":
      return "warning";
    default:
      return "secondary";
  }
};

export default useNotificationDeliveryReport;
